import type { SlideDeck } from './slideData';
import { BAND1_MONTHLY, BAND1_ANNUAL, ONBOARDING_BASE, MIN_USERS } from '../lib/pricingConstants';

const customCSS = `
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');

.dark-theme .reveal { font-family: 'DM Sans', sans-serif; }
.dark-theme .reveal h1, .dark-theme .reveal h2 {
	font-family: 'DM Sans', sans-serif;
	color: #e8e8f0;
	text-transform: none;
	letter-spacing: -0.02em;
	font-weight: 700;
}
.dark-theme .reveal h1 { font-size: 2.6em; line-height: 1.1; }
.dark-theme .reveal h2 { font-size: 1.8em; line-height: 1.2; margin-bottom: 0.6em; }
.dark-theme .reveal h3 { font-size: 1.2em; line-height: 1.3; color: #E8491C; font-weight: 600; font-family: 'DM Sans', sans-serif; }
.dark-theme .reveal p, .dark-theme .reveal li { font-size: 0.72em; line-height: 1.6; color: #f0f0f5; }
.dark-theme .reveal ul { list-style: none; padding-left: 0; }
.dark-theme .reveal ul li { padding: 0.3em 0 0.3em 1.4em; position: relative; }
.dark-theme .reveal ul li::before { content: ''; position: absolute; left: 0; top: 0.75em; width: 6px; height: 6px; border-radius: 50%; background: #E8491C; }
.dark-theme .reveal .slides section { padding: 40px 60px; text-align: left; }

.st-accent { width: 60px; height: 4px; background: #E8491C; border-radius: 2px; margin-bottom: 1.2em; }

.st-bq { background: rgba(255,255,255,0.04); border-left: 4px solid #E8491C; padding: 0.8em 1.2em; margin: 0.8em 0; border-radius: 0 8px 8px 0; width: 90%; box-shadow: none; }
.st-bq p { color: #f0f0f5 !important; font-size: 0.78em !important; font-weight: 400; }

.st-section { text-align: center !important; }
.st-section-num { font-family: 'JetBrains Mono', monospace; font-size: 0.6em; color: #E8491C; font-weight: 500; margin-bottom: 0.4em; display: block; }

.st-icon { display: inline-flex; align-items: center; justify-content: center; width: 44px; height: 44px; border-radius: 10px; font-size: 1.2em; margin-bottom: 12px; }
.st-icon-purple { background: rgba(232,73,28,0.15); }
.st-icon-green { background: rgba(34,197,94,0.15); }
.st-icon-blue { background: rgba(59,130,246,0.15); }
.st-icon-amber { background: rgba(245,158,11,0.15); }

.st-card-row { display: flex; gap: 20px; margin-top: 0.8em; }
.st-card { flex: 1; background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.06); border-radius: 12px; padding: 24px; }
.st-card h3 { margin-top: 0; margin-bottom: 0.3em; font-size: 1em; }
.st-card .card-q { font-size: 0.65em; color: #9898b0; font-style: italic; margin-bottom: 0.6em; }
.st-card p, .st-card li { font-size: 0.62em; }

.st-pricing-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-top: 0.8em; }
.st-price-card { background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.06); border-radius: 12px; padding: 24px; text-align: center; }
.st-price-card.highlight { border-color: #E8491C; background: rgba(232,73,28,0.06); }
.st-price-card .label { font-size: 0.6em; color: #9898b0; text-transform: uppercase; letter-spacing: 0.08em; font-weight: 600; }
.st-price-card .amount { font-size: 1.8em; font-weight: 700; color: #e8e8f0; margin: 0.2em 0; }
.st-price-card .amount .currency { font-size: 0.6em; vertical-align: top; }
.st-price-card .detail { font-size: 0.55em; color: #9898b0; }

.st-big-stat { font-size: 1.875em; font-weight: 700; color: #E8491C; line-height: 1; margin: 0.2em 0; }

.st-built-for { display: flex; gap: 16px; margin-top: 1em; flex-wrap: wrap; }
.st-built-for-item { flex: 1; min-width: 180px; background: rgba(232,73,28,0.06); border: 1px solid rgba(232,73,28,0.15); border-radius: 10px; padding: 16px; text-align: center; }
.st-built-for-item .emoji { font-size: 1.4em; margin-bottom: 6px; }
.st-built-for-item .name { font-size: 0.7em; font-weight: 600; color: #e8e8f0; }

.st-compact li { padding: 0.15em 0 0.15em 1.4em !important; }
.st-compact li::before { top: 0.55em !important; }
`;

// Derived pricing for slides (5-10 person team, monthly)
const saasLow = (5 * BAND1_MONTHLY * 12).toLocaleString();
const saasHigh = (10 * BAND1_MONTHLY * 12).toLocaleString();
const yearOneLow = (5 * BAND1_MONTHLY * 12 + ONBOARDING_BASE).toLocaleString();
const yearOneHigh = (10 * BAND1_MONTHLY * 12 + ONBOARDING_BASE).toLocaleString();
const onboardingDisplay = ONBOARDING_BASE.toLocaleString();

const minMonthly = (MIN_USERS * BAND1_MONTHLY).toLocaleString();

export const smallTeamsPresentation: SlideDeck = {
	slug: 'livewire-small-teams',
	name: 'Small Teams',
	subtitle: 'Planning department capabilities without the planning department',
	color: '#1a1a2e',
	darkTheme: true,
	customCSS,
	slides: [
		// 1 — Title
		{
			title: '',
			background: '#1a1a2e',
			content: `
				<h1 style="font-size:3em;margin-bottom:0.1em;font-family:lvwr-ubuntu-thin,sans-serif;">
					<span style="color:#E8491C;font-weight:700;">Livewire</span>
				</h1>
				<p style="font-size:1.1em;color:#f0f0f5;font-weight:400;margin-top:0.3em;margin-bottom:0.5em;">
					Big Utility Capabilities. Small Utility Budget.
				</p>
				<p style="font-size:0.8em;color:#9898b0;margin-bottom:1.5em;">
					Planning department capabilities without the planning department.
				</p>
				<div class="fragment" style="font-size:0.6em;color:#f0f0f5;border-top:1px solid rgba(255,255,255,0.08);padding-top:1em;margin-top:1em;">
					<br>Rhys Stewart <br> Auchindown<br>
				</div>`,
		},
		// 2 — Section 01
		{
			title: '',
			background: '#1a1a2e',
			content: `
				<div class="st-section">
					<span class="st-section-num">01</span>
					<h2 style="font-size:2.2em;">The Small Team Reality</h2>
					<p style="max-width:600px;margin:0 auto;">Doing more with less isn't a strategy — it's a daily reality</p>
				</div>`,
		},
		// 3 — Challenges
		{
			title: '',
			background: '#1a1a2e',
			content: `
				<div class="st-accent"></div>
				<h2>The gap between expectations and resources</h2>
				<ul>
					<li class="fragment"><strong style="color:#e8e8f0;">Everyone wears multiple hats</strong> — your engineers handle planning, operations, and projects. There's no dedicated team for network modeling or reliability analysis</li>
					<li class="fragment"><strong style="color:#e8e8f0;">Enterprise tools, enterprise prices</strong> — CYME, Milsoft, Esri's full stack — designed for utilities with 50+ person planning departments and budgets to match</li>
					<li class="fragment"><strong style="color:#e8e8f0;">Consultants don't know your system</strong> — $200/hour for someone who needs three weeks to understand your network. Then they leave and take the knowledge with them</li>
					<li class="fragment"><strong style="color:#e8e8f0;">Same expectations, fewer resources</strong> — regulators and boards still want reliability data, investment justifications, and scenario analysis. They don't care about your headcount</li>
				</ul>`,
		},
		// 4 — The real cost
		{
			title: '',
			background: '#1a1a2e',
			content: `
				<div class="st-accent"></div>
				<h2>The real cost of not having the right tools</h2>
				<div class="st-card-row">
					<div class="st-card">
						<h3>📊 Reactive decisions</h3>
						<p>Without modeling capability, every capital decision is based on intuition or outdated spreadsheets. Investments are hard to justify and harder to prioritize.</p>
					</div>
					<div class="st-card">
						<h3>📋 Lost knowledge</h3>
						<p>The engineer who knows every splice and every workaround retires. That institutional knowledge walks out the door — and it's gone.</p>
					</div>
				</div>
				<div class="st-bq fragment" style="margin-top:1em;">
					<p>Small teams can't afford to guess. But they also can't afford the tools that eliminate guessing.</p>
				</div>`,
		},
		// 5 — Section 02
		{
			title: '',
			background: '#1a1a2e',
			content: `
				<div class="st-section">
					<span class="st-section-num">02</span>
					<h2 style="font-size:2.2em;"><span style="font-family:lvwr-ubuntu-thin,sans-serif;color:#E8491C;">Livewire</span> as Force Multiplier</h2>
					<p style="max-width:600px;margin:0 auto;">You don't need more people. You need better tools.</p>
				</div>`,
		},
		// 6 — What is Livewire
		{
			title: '',
			background: '#1a1a2e',
			content: `
				<div class="st-accent"></div>
				<h2>A cloud-based spatial digital twin</h2>
				<ul>
					<li class="fragment">A platform for <strong style="color:#e8e8f0;">modeling, analyzing, and documenting</strong> your distribution network</li>
					<li class="fragment">Gives a two-person engineering team the analytical capability that used to require dedicated planning staff</li>
					<li class="fragment">Browser-based — no GIS software, no desktop installation, no IT department required</li>
					<li class="fragment">Mobile ready — inspect assets and annotate findings directly from a phone or tablet in the field</li>
				</ul>
				<div class="st-bq fragment" style="margin-top:1em;">
					<p>Model your network. Calculate reliability. Justify investments. All without adding headcount.</p>
				</div>`,
		},
		// 7 — Section 03
		{
			title: '',
			background: '#1a1a2e',
			content: `
				<div class="st-section">
					<span class="st-section-num">03</span>
					<h2 style="font-size:2.2em;">Your Toolkit</h2>
					<p style="max-width:600px;margin:0 auto;">Five capabilities that change what a small team can do</p>
				</div>`,
		},
		// 8 — Instant Reliability Metrics
		{
			title: '',
			background: '#1a1a2e',
			content: `
				<div class="st-icon st-icon-purple">📊</div>
				<h3>Instant Reliability Metrics</h3>
				<p style="font-size:0.78em;margin-bottom:0.6em;">
					Calculate SAIDI, SAIFI, and other indices without building spreadsheets or hiring consultants.
				</p>
				<ul>
					<li class="fragment">Derive reliability indices directly from network topology</li>
					<li class="fragment">Break down performance by feeder, zone, or any spatial boundary</li>
					<li class="fragment">Answer regulator questions the same day — not next quarter</li>
				</ul>
				<p class="fragment" style="margin-top:0.8em;font-size:0.65em;">
					<strong style="color:#E8491C;">Before Livewire:</strong> weeks of spreadsheet work or a consultant engagement<br>
					<strong style="color:#E8491C;">With Livewire:</strong> minutes
				</p>`,
		},
		// 9 — Test Before You Invest
		{
			title: '',
			background: '#1a1a2e',
			content: `
				<div class="st-icon st-icon-green">🔄</div>
				<h3>Test Before You Invest</h3>
				<p style="font-size:0.78em;margin-bottom:0.6em;">
					Model capital projects and see the reliability impact before committing budget.
				</p>
				<ul>
					<li class="fragment">What happens if we reconductor this feeder? Add a tie switch? Install a recloser?</li>
					<li class="fragment">Compare scenarios side by side — quantify the ROI of each option</li>
					<li class="fragment">Walk into board meetings with data, not intuition</li>
				</ul>
				<p class="fragment" style="margin-top:0.8em;font-size:0.65em;">
					<strong style="color:#E8491C;">Before Livewire:</strong> gut feeling and spreadsheets<br>
					<strong style="color:#E8491C;">With Livewire:</strong> modeled scenarios with quantified impact
				</p>`,
		},
		// 10 — No Infrastructure
		{
			title: '',
			background: '#1a1a2e',
			content: `
				<div class="st-icon st-icon-blue">☁️</div>
				<h3>No Infrastructure to Manage</h3>
				<p style="font-size:0.78em;margin-bottom:0.6em;">
					Cloud-based. No servers to maintain, no software to install, no IT overhead.
				</p>
				<ul>
					<li class="fragment">Access from anywhere with a browser — office, field, home</li>
					<li class="fragment">Automatic updates — always the latest version, zero maintenance</li>
					<li class="fragment">Your data is secure, backed up, and accessible 24/7</li>
				</ul>
				<div class="st-bq fragment" style="margin-top:0.8em;">
					<p>Enterprise tools need enterprise IT. Livewire just needs a browser.</p>
				</div>`,
		},
		// 11 — Capture Field Knowledge
		{
			title: '',
			background: '#1a1a2e',
			content: `
				<div class="st-icon st-icon-amber">📸</div>
				<h3>Capture Field Knowledge</h3>
				<p style="font-size:0.78em;margin-bottom:0.6em;">
					Your small team knows the system better than anyone. Document that knowledge before it walks out the door.
				</p>
				<ul>
					<li class="fragment">Photos, comments, and observations — geolocated and timestamped, tied to specific assets</li>
					<li class="fragment">Build a living record of field conditions that survives staff turnover</li>
					<li class="fragment">New hires inherit decades of institutional knowledge on day one</li>
				</ul>
				<p class="fragment" style="margin-top:0.8em;font-size:0.65em;">
					<strong style="color:#E8491C;">The problem isn't that small teams lack knowledge.</strong> It's that the knowledge lives in people's heads instead of in a system.
				</p>`,
		},
		// 12 — Network Snapshots
		{
			title: '',
			background: '#1a1a2e',
			content: `
				<div class="st-icon st-icon-purple">📸</div>
				<h3>Network Snapshots — "What did it look like then?"</h3>
				<p style="font-size:0.78em;margin-bottom:0.6em;">
					Capture point-in-time snapshots of your entire network. Compare any two moments in history.
				</p>
				<ul>
					<li class="fragment">Before and after a capital project — prove what you built and show the impact</li>
					<li class="fragment">Pre-storm baseline vs. post-storm reality — spatial evidence, not damage reports</li>
					<li class="fragment">Calculate reliability metrics on past configurations — show improvement over time</li>
				</ul>
				<div class="st-bq fragment" style="margin-top:0.8em;">
					<p>Your GIS only shows today. Livewire shows yesterday, last year, and the day before the storm.</p>
				</div>`,
		},
		// 13 — Built for teams like yours
		{
			title: '',
			background: '#1a1a2e',
			content: `
				<div class="st-accent"></div>
				<h2>Built for teams like yours</h2>
				<div class="st-built-for">
					<div class="st-built-for-item">
						<div class="emoji">🏝️</div>
						<div class="name">Island &amp; isolated utilities</div>
					</div>
					<div class="st-built-for-item">
						<div class="emoji">👥</div>
						<div class="name">Small engineering teams</div>
					</div>
					<div class="st-built-for-item">
						<div class="emoji">💡</div>
						<div class="name">Co-ops &amp; municipal utilities</div>
					</div>
					<div class="st-built-for-item">
						<div class="emoji">📋</div>
						<div class="name">Resource-constrained regulators</div>
					</div>
				</div>
				<div class="st-bq fragment" style="margin-top:1.2em;">
					<p>Livewire gives a two-person engineering team the analytical capability that used to require dedicated planning staff.</p>
				</div>`,
		},
		// 13 — Demo
		{
			title: '',
			background: '#0f0f1e',
			content: `
				<div class="st-section">
					<span class="st-section-num">04</span>
					<div style="display:inline-block;background:#E8491C;color:white;font-size:0.5em;font-weight:600;padding:4px 14px;border-radius:20px;text-transform:uppercase;letter-spacing:0.1em;margin-bottom:0.6em;">Live Demo</div>
					<h2 style="font-size:2.2em;">See What's Possible</h2>
					<p style="max-width:600px;margin:0 auto;">See how Livewire can extend your team's capabilities</p>
				</div>`,
		},
		// Closing
		{
			title: '',
			background: '#1a1a2e',
			content: `
				<div class="st-section">
					<h1 style="margin-bottom:0.2em;font-size:3em;font-family:lvwr-ubuntu-thin,sans-serif;">
						<span style="color:#E8491C;">Livewire</span>
					</h1>
					<p style="font-size:0.85em;color:#9898b0;margin-bottom:1.5em;">Do more with the team you have.</p>
					<p style="font-size:0.7em;color:#e8e8f0;">
						Rhys Stewart<br>
						<span style="color:#9898b0;">rhys@auchindown.com · lvwr.io</span>
					</p>
				</div>`,
			verticalSlides: [
				{
					title: '',
					background: '#1a1a2e',
					content: `
						<div class="st-accent"></div>
						<h2>Simple, accessible pricing</h2>
						<div class="st-card" style="text-align:center;border-color:rgba(232,73,28,0.3);background:rgba(232,73,28,0.06);">
							<p class="card-q">Starting price</p>
							<div class="st-big-stat" style="font-size:3.2em;">$${minMonthly}<span style="font-size:0.28em;color:#9898b0;font-weight:600;"> / month</span></div>
							<p>Minimum ${MIN_USERS} users at $${BAND1_MONTHLY}/user, then scale up</p>
						</div>
						<div class="st-card-row" style="margin-top:16px;">
							<div class="st-card" style="text-align:center;">
								<p class="card-q">Monthly</p>
								<div class="st-big-stat">$${BAND1_MONTHLY}</div>
								<p>per user / month</p>
							</div>
							<div class="st-card" style="text-align:center;">
								<p class="card-q">Annual commitment</p>
								<div class="st-big-stat">$${BAND1_ANNUAL}</div>
								<p>per user / month · 10+ users</p>
							</div>
						</div>
						<p style="margin-top:0.9em;font-size:0.62em;text-align:center;">
							100 GB storage included · No GIS licensing required · Browser-based
						</p>`,
				},
				{
					title: '',
					background: '#1a1a2e',
					content: `
						<div class="st-accent"></div>
						<h2>For a 5 to 10 person team</h2>
						<div class="st-card-row">
							<div class="st-card" style="text-align:center;">
								<p class="card-q">SaaS</p>
								<div class="st-big-stat">$${saasLow} – $${saasHigh}</div>
								<p>per year</p>
							</div>
							<div class="st-card" style="text-align:center;">
								<p class="card-q">Onboarding (one-time)</p>
								<div class="st-big-stat">$${onboardingDisplay}+</div>
								<p>Jumpstart — 2 weeks to go-live</p>
							</div>
						</div>
						<div class="st-card" style="margin-top:16px;text-align:center;border-color:rgba(232,73,28,0.2);">
							<p class="card-q">Year 1 all-in</p>
							<div class="st-big-stat">$${yearOneLow} – $${yearOneHigh}</div>
							<p>Less than a single consultant engagement — permanent capability</p>
						</div>`,
				},
			],
		},
	],
};
